import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import TimesheetFormModal from '../components/TimesheetFormModal';
import AttendanceFormModal from '../components/AttendanceFormModal';
import './EmployeeDetail.css';
import { toast } from '../components/Toast';
import { formatCurrency } from '../utils/currency';

function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [activeTab, setActiveTab] = useState('timesheets');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);

  useEffect(() => {
    fetchEmployeeDetails();
    fetchTimesheets();
    fetchAttendance();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/employees/${id}`);
      const data = response.data.data || response.data;
      setEmployee(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch employee details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheets = async () => {
    try {
      const response = await axios.get(`/employees/${id}/timesheets`);
      const timesheetData = response.data.timesheets || response.data.data?.timesheets || [];
      setTimesheets(timesheetData);
    } catch (err) {
      console.error('Failed to fetch timesheets:', err);
    }
  };

  const fetchAttendance = async () => {
    try {
      const response = await axios.get(`/employees/${id}/attendance`);
      const attendanceData = response.data.data || response.data || [];
      setAttendance(attendanceData);
    } catch (err) {
      console.error('Failed to fetch attendance:', err);
    }
  };

  const handleSubmitTimesheet = () => {
    setShowTimesheetModal(true);
  };

  const handleRecordAttendance = () => {
    setShowAttendanceModal(true);
  };

  const handleTimesheetModalClose = () => {
    setShowTimesheetModal(false);
  };

  const handleAttendanceModalClose = () => {
    setShowAttendanceModal(false);
  };

  const handleTimesheetSuccess = () => {
    setShowTimesheetModal(false);
    fetchTimesheets();
    showSuccessMessage('Timesheet submitted successfully');
  };

  const handleAttendanceSuccess = () => {
    setShowAttendanceModal(false);
    fetchAttendance();
    showSuccessMessage('Attendance recorded successfully');
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };



  const getStatusBadge = (status) => {
    const statusClasses = {
      PRESENT: 'badge-success',
      ABSENT: 'badge-danger',
      HALF_DAY: 'badge-warning',
      LEAVE: 'badge-info'
    };
    return <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>{status}</span>;
  };

  if (loading) {
    return <div className="loading">Loading employee details...</div>;
  }

  if (!employee) {
    return <div className="error">Employee not found</div>;
  }

  return (
    <div className="employee-detail-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/employees')}>
          ← Back to Employees
        </button>
        <h1>Employee Details</h1>
        <button className="btn btn-outline" style={{ marginLeft: 'auto' }}
          onClick={async () => {
            if (!window.confirm('Reset this employee\'s password?')) return;
            try {
              const res = await axios.put(`/employees/${id}/reset-password`, {});
              const data = res.data.data || res.data;
              toast.success(`Password reset: ${data.newPassword} — share with employee.`);
            } catch (err) {
              toast.error(err.response?.data?.message || 'Failed to reset password');
            }
          }}>
          🔑 Reset Password
        </button>
      </div>

      <div className="employee-info-card">
        <div className="info-grid">
          <div className="info-item">
            <label>Employee Code:</label>
            <span>{employee.employeeCode}</span>
          </div>
          <div className="info-item">
            <label>Name:</label>
            <span>{employee.userName}</span>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <span>{employee.userEmail || '-'}</span>
          </div>
          <div className="info-item">
            <label>Role:</label>
            <span>{employee.customRoleName || employee.userRole || '-'}</span>
          </div>
          <div className="info-item">
            <label>Branch:</label>
            <span>{employee.branchName || (employee.branchId ? `Branch #${employee.branchId}` : '-')}</span>
          </div>
          <div className="info-item">
            <label>Manager:</label>
            <span>{employee.managerName || (employee.managerId ? `Employee #${employee.managerId}` : '-')}</span>
          </div>
          <div className="info-item">
            <label>Designation:</label>
            <span>{employee.designation || '-'}</span>
          </div>
          <div className="info-item">
            <label>Department:</label>
            <span>{employee.department || '-'}</span>
          </div>
          <div className="info-item">
            <label>Join Date:</label>
            <span>{new Date(employee.joinDate).toLocaleDateString()}</span>
          </div>
          <div className="info-item">
            <label>Base Salary:</label>
            <span>{formatCurrency(employee.baseSalary)}</span>
          </div>
          <div className="info-item">
            <label>Bank Account:</label>
            <span>{employee.bankAccount || '-'}</span>
          </div>
          <div className="info-item">
            <label>Status:</label>
            <span className={`status-badge ${employee.active ? 'active' : 'inactive'}`}>
              {employee.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {employee.plainPassword && (
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <label>Current Password (admin view):</label>
              <span style={{ fontFamily: 'monospace', background: '#fef9c3', padding: '2px 8px', borderRadius: '4px', border: '1px solid #fde047', color: '#713f12' }}>
                {employee.plainPassword}
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#92400e' }}>
                  — employee hasn't changed it yet
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'timesheets' ? 'active' : ''}`}
            onClick={() => setActiveTab('timesheets')}
          >
            Timesheets ({timesheets.length})
          </button>
          <button
            className={`tab ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            Attendance ({attendance.length})
          </button>
          <button
            className={`tab ${activeTab === 'payroll' ? 'active' : ''}`}
            onClick={() => setActiveTab('payroll')}
          >
            Payroll
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'timesheets' && (
            <div className="timesheets-tab">
              <div className="tab-header">
                <button className="btn btn-primary" onClick={handleSubmitTimesheet}>
                  Submit Timesheet
                </button>
              </div>
              {timesheets.length === 0 ? (
                <div className="no-data">No timesheets found for this employee</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Work Date</th>
                        <th>Project</th>
                        <th>Hours Worked</th>
                        <th>Description</th>
                        <th>Approved By</th>
                        <th>Submitted At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timesheets.map((timesheet) => (
                        <tr key={timesheet.id}>
                          <td>{timesheet.id}</td>
                          <td>{new Date(timesheet.workDate).toLocaleDateString()}</td>
                          <td>{timesheet.projectName || '-'}</td>
                          <td>{timesheet.hoursWorked}</td>
                          <td>{timesheet.description || '-'}</td>
                          <td>{timesheet.approvedByName || 'Pending'}</td>
                          <td>{new Date(timesheet.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="attendance-tab">
              <div className="tab-header">
                <button className="btn btn-primary" onClick={handleRecordAttendance}>
                  Record Attendance
                </button>
              </div>
              {attendance.length === 0 ? (
                <div className="no-data">No attendance records found for this employee</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Date</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                        <th>Status</th>
                        <th>Recorded At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.map((record) => (
                        <tr key={record.id}>
                          <td>{record.id}</td>
                          <td>{new Date(record.attendanceDate).toLocaleDateString()}</td>
                          <td>{record.checkInTime || '-'}</td>
                          <td>{record.checkOutTime || '-'}</td>
                          <td>{getStatusBadge(record.status)}</td>
                          <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'payroll' && (
            <div className="payroll-tab">
              <div className="no-data">Payroll information will be available in the Payroll module</div>
            </div>
          )}
        </div>
      </div>

      {showTimesheetModal && (
        <TimesheetFormModal
          employeeId={id}
          onClose={handleTimesheetModalClose}
          onSuccess={handleTimesheetSuccess}
        />
      )}

      {showAttendanceModal && (
        <AttendanceFormModal
          employeeId={id}
          onClose={handleAttendanceModalClose}
          onSuccess={handleAttendanceSuccess}
        />
      )}
    </div>
  );
}

export default EmployeeDetail;
